/**
 * The page's half (unit 25B; unit 26 calls `startOffline` from the shell).
 *
 * The drain runs on the Service Worker's `sync` where the browser has
 * Background Sync (Chrome, the phone), and on `online` and on open everywhere
 * (rule 8). A new Service Worker takes over at once, and the page reloads once
 * when it does (v1.84) — the outbox is in IndexedDB, so the reload loses
 * nothing. Guarded on a controller already being there, so a first visit does
 * not reload.
 */

import { openOffline } from "./idb";
import { SYNC_TAG, sync, type Synced } from "./sync";

interface SyncManager {
  register(tag: string): Promise<void>;
}

/** Drain now, in the page. */
export async function syncNow(): Promise<Synced> {
  const db = await openOffline();
  try {
    return await sync(db, fetch);
  } finally {
    db.close();
  }
}

/**
 * After a write is queued: ask the Service Worker to drain when there is
 * signal, or drain now where the browser cannot be asked.
 */
export async function requestDrain(): Promise<void> {
  const registration = await navigator.serviceWorker?.ready;
  const manager = (registration as { sync?: SyncManager } | undefined)?.sync;
  if (manager) {
    try {
      await manager.register(SYNC_TAG);
      return;
    } catch {
      // Refused (a permission, a private window): drain here instead.
    }
  }
  if (navigator.onLine) await syncNow();
}

/**
 * Returns a function that stops the page listening. `worker: false` leaves the
 * Service Worker out — Vite's dev server has no `sw.js` to register.
 */
export function startOffline(
  onSynced?: (result: Synced) => void,
  { worker = true }: { worker?: boolean } = {},
): () => void {
  const round = () => {
    void syncNow().then((result) => onSynced?.(result));
  };
  if (worker && "serviceWorker" in navigator) {
    const hadController = Boolean(navigator.serviceWorker.controller);
    let reloading = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadController || reloading) return;
      reloading = true;
      window.location.reload();
    });
    void navigator.serviceWorker.register("/sw.js", { type: "module" });
  }
  window.addEventListener("online", round);
  round();
  return () => window.removeEventListener("online", round);
}
